import axios from "axios";
import {
  forceLogout,
  recordRequestFailure,
  recordRequestSuccess,
  shouldSkipForUrl,
} from "./sessionRecovery";
import { injectable } from "inversify";
import "reflect-metadata";
import {
  ContainerResponse,
  LoginResponse,
  MetaData,
  NotificationResponse,
  PaginationCollection,
  PositionResponse,
  PublicPositionResponse,
  SendMessageRequest,
} from "@/models/dtos";
import type {
  IEmploymentService,
  IEmploymentHttpClient,
} from "@/interfaces/IEmploymentService";
import type { IJobPositionResource } from "@/interfaces/IJobPositionResource";
import type { IContainerResource } from "@/interfaces/IContainerResource";
import { TYPES } from "@/types";
import { GetUserByIdResponse } from "@/models/GetUserByIdResponse";

import { useCookies } from "@vueuse/integrations/useCookies";

@injectable()
export class EmploymentHttpClient implements IEmploymentHttpClient {
  private client: any;
  constructor() {
    this.client = this.createClient("", true);
  }

  createClient(token: string, includeAuthorization: boolean): any {
    const headers: any = {
      "Content-Type": "application/json",
    };

    if (includeAuthorization) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const _axios = axios.create({
      baseURL: TYPES.BaseURL,
      headers: headers,
    });
    _axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("user_token");

        if (token) {
          config.headers["Authorization"] = "Bearer " + token;
        }
        config.headers["Content-Type"] = "application/json";
        return config;
      },
      (error) => {
        Promise.reject(error);
      }
    );

    // Response-interceptor: ANSKNI-22 (auth-failure auto-logout) +
    // ANSKNI-56 #2 (consecutive 5xx-burst-detector). Skip-listan undantar
    // login/public-flöden så de aldrig kan trigga en redirect-loop.
    _axios.interceptors.response.use(
      (response) => {
        if (!shouldSkipForUrl(response?.config?.url)) {
          recordRequestSuccess();
        }
        return response;
      },
      (error) => {
        const url = error?.config?.url;
        if (!shouldSkipForUrl(url)) {
          // ANSKNI-142: auto-logout återaktiverat men ENBART för 401.
          //
          // Hotfixen 2026-05-12 stängde av 401/403-logout eftersom den var
          // för aggressiv: 403 (permission denied på en specifik resurs)
          // behandlades som expired session → re-login gav ingen ny
          // behörighet → logout-loop. Lösningen hotfixen efterlyste är att
          // skilja 401 (token ogiltig/utgången, inkl. JWT-nyckelrotation
          // mid-session) från 403 (permission). Re-login löser ett 401 men
          // inte ett 403, så bara 401 loggar ut.
          const status = error?.response?.status;
          if (status === 401) {
            forceLogout("session-expired");
          } else {
            // 403 och övriga fel: ingen logout. 5xx-burst-detektorn körs
            // vidare och kan trigga "connection-lost" vid backend-haveri.
            recordRequestFailure(error);
          }
        }
        return Promise.reject(error);
      }
    );

    return _axios;
  }

  updateClient(token: string): void {
    this.client = this.createClient(token, true);
  }

  async getUrl(url: string): Promise<any> {
    return this.client.get(url);
  }
  async postUrl(url: string, data: any): Promise<any> {
    return this.client.post(url, data);
  }
  async post(data: any): Promise<any> {
    return this.client.post("/api/dispatcher", data);
  }
}
@injectable()
export class EmploymentService implements IEmploymentService {
  private _username = "";
  private _password = "";
  private _login: LoginResponse = new LoginResponse();

  JobPosition: IJobPositionResource;
  Container: IContainerResource;

  constructor(
    positionResouse: IJobPositionResource,
    containerResource: IContainerResource,
    private httpClient: IEmploymentHttpClient
  ) {
    this.JobPosition = positionResouse;
    this.Container = containerResource;
  }
  async deleteNotificationById(id: string, containerId: number): Promise<any> {
    const data = {
      action: "delete_NotificationById",
      data: {
        containerId: containerId,
        id: id,
      },
    };
    return await this.httpClient.post(data);
  }
  async sendMessage(v: SendMessageRequest): Promise<any[]> {
    const data = {
      action: "send_MessageCommand",
      data: v,
    };
    return await this.httpClient.post(data);
  }

  async getGetMessagingChannelsQuery(
    containerId: number
  ): Promise<NotificationResponse[]> {
    const data = {
      action: "get_MessagingChannels",
      data: {
        containerId: containerId,
      },
    };
    return await this.httpClient.post(data);
  }

  // ANSKNI-107: fetch the stripped external view of a single applicant. Backend
  // (PermissionDecorator) only resolves applicants the external viewer is allowed
  // to see, so no extra client-side scoping is needed here.
  async getExternalApplicant(id: number): Promise<any> {
    const data = {
      action: "get_externalApplicant",
      data: {
        id: id,
      },
    };
    return await this.httpClient.post(data);
  }

  // ANSKNI-108: invite Bolag B to a container with scoped ExternalViewer access.
  async inviteExternalClient(
    email: string,
    containerId: number,
    expiresAt: string | null = null
  ): Promise<any> {
    const data = {
      action: "invite_externalClient",
      data: {
        email: email,
        containerId: containerId,
        expiresAt: expiresAt,
      },
    };
    return await this.httpClient.post(data);
  }

  async createApplicant(email: string, positionId: number, metadata: MetaData) {
    const data = {
      action: "create_jobApplication",
      data: {
        email: email,
        positionId: positionId,
        metadata: metadata,
      },
    };
    return await this.httpClient.postUrl(
      TYPES.BaseURL + "/api/public/positions/apply",
      data
    );
  }

  async getPublicPositions(
    take: number,
    page: number,
    containerId?: number
  ): Promise<PaginationCollection<PositionResponse[]>> {
    const url = containerId
      ? `/api/public/positions?containerId=${containerId}&take=${take}&page=${page}`
      : `/api/public/positions?take=${take}&page=${page}`;

    const response = await this.httpClient.getUrl(TYPES.BaseURL + url);
    return response.data;
  }
  async getPublicPosition(
    id: number | string
  ): Promise<PublicPositionResponse> {
    const response = await this.httpClient.getUrl(
      TYPES.BaseURL + "/api/public/positions/" + id
    );
    return response.data;
  }
  async getContainerByName(name: string): Promise<ContainerResponse> {
    const response = await this.httpClient.getUrl(
      TYPES.BaseURL + "/api/public/containers/" + name
    );
    return response.data;
  }
  async getContainerByPositionId(id: number): Promise<ContainerResponse> {
    const response = await this.httpClient.getUrl(
      TYPES.BaseURL + "/api/public/open/" + id + "/container"
    );
    return response.data;
  }
  async login(username: string, password: string): Promise<LoginResponse> {
    this._username = username;
    this._password = password;

    const url = "/api/account/login";
    const model = {
      email: this._username,
      password: this._password,
    };

    const tempClient = new EmploymentHttpClient().createClient("", false);
    const response = await tempClient.post(url, model);
    localStorage.setItem("user_token", response.data.token);
    const loginResponse: LoginResponse = response.data;
    this.httpClient.updateClient(loginResponse.token);
    return loginResponse;
  }

  async getUserById(userId: string): Promise<GetUserByIdResponse | null> {
    try {
      const data = {
        action: "GetUserByIdQuery",
        data: {
          userId: userId,
        },
      };
      const response = await this.httpClient.post(data);
      return response.data || null;
    } catch (error) {
      console.error("Failed to get user by ID:", error);
      return null;
    }
  }
}
